import { useEffect, useState } from "react";
import { Box, Flex, Text, Button, Icon, HStack, Stack } from "@chakra-ui/react";
import { Share, PlusSquare, Download, X, Smartphone } from "lucide-react";
import { colors } from "@/theme/colors";
import { logWarn } from "@/lib/log";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports a Mac user agent but has a touchscreen
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)) &&
  !window.MSStream;
const isAndroid = () => /android/i.test(navigator.userAgent);

// Chrome fires beforeinstallprompt right after page load — before login, before
// this component mounts. Capture it at module scope so it's never missed.
let capturedPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    capturedPrompt = e;
    window.dispatchEvent(new CustomEvent("pwa-installable"));
  });
}

// Non-sensitive UI preference only: a "1" flag remembering the user dismissed
// the install banner. No tokens, credentials or personal data are stored here.
const DISMISS_KEY = "hakina_pwa_prompt_dismissed_v2";
const readDismissed = () => {
  try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
};
const persistDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch (e) {
    logWarn("InstallPrompt: could not persist dismissal (private mode?) — banner returns next visit", e);
  }
};

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(() => capturedPrompt);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onInstallable = () => setDeferred(capturedPrompt);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  const ios = isIOS();
  const android = isAndroid();
  if (!ios && !android && !deferred) return null; // desktop without install support

  const dismiss = () => { persistDismissed(); setDismissed(true); };

  return (
    <Box position="fixed" bottom={{ base: "16", md: "4" }} left="4" right={{ base: "4", md: "auto" }}
      maxW={{ md: "380px" }} zIndex={100} bg={colors.white} border="1px solid" borderColor={colors.border}
      borderRadius="14px" boxShadow="0 10px 32px rgba(31,41,55,0.18)" p="4" data-testid="install-prompt">
      <Flex justify="space-between" align="flex-start" gap="2" mb="2">
        <HStack gap="2">
          <Icon as={Smartphone} boxSize="5" color={colors.teal} />
          <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>Add Hakina to your home screen</Text>
        </HStack>
        <Button size="2xs" variant="ghost" onClick={dismiss} data-testid="install-dismiss-btn"><X size={14} /></Button>
      </Flex>
      {ios ? (
        <Stack gap="1.5" data-testid="ios-install-steps">
          <Text fontSize="sm" color={colors.textSecondary}>Install it like a real app — in Safari:</Text>
          <HStack gap="2">
            <Icon as={Share} boxSize="4" color={colors.teal} />
            <Text fontSize="sm" color={colors.textPrimary}>1. Tap the <b>Share</b> button below</Text>
          </HStack>
          <HStack gap="2">
            <Icon as={PlusSquare} boxSize="4" color={colors.teal} />
            <Text fontSize="sm" color={colors.textPrimary}>2. Choose <b>Add to Home Screen</b></Text>
          </HStack>
          <Text fontSize="sm" color={colors.textPrimary} pl="6">3. Tap <b>Add</b> — done!</Text>
        </Stack>
      ) : deferred ? (
        <>
          <Text fontSize="sm" color={colors.textSecondary} mb="3">Install the app for full-screen use and quicker access in the field.</Text>
          <Button size="sm" w="full"
            data-testid="android-install-btn"
            onClick={async () => { deferred.prompt(); const { outcome } = await deferred.userChoice; if (outcome !== "dismissed") setInstalled(true); setDeferred(null); }}>
            <Download size={15} /> Install Hakina Safety
          </Button>
        </>
      ) : (
        <Stack gap="1.5" data-testid="android-manual-steps">
          <Text fontSize="sm" color={colors.textSecondary}>Install it like a real app — in Chrome:</Text>
          <Text fontSize="sm" color={colors.textPrimary}>1. Tap the <b>⋮ menu</b> (top right of Chrome)</Text>
          <Text fontSize="sm" color={colors.textPrimary}>2. Choose <b>Add to Home screen</b> (or <b>Install app</b>)</Text>
          <Text fontSize="sm" color={colors.textPrimary}>3. Tap <b>Install</b> — done!</Text>
        </Stack>
      )}
    </Box>
  );
};

export const UpdateBanner = () => {
  const [reg, setReg] = useState(null);
  useEffect(() => {
    const handler = (e) => setReg(e.detail);
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);
  if (!reg?.waiting) return null;
  return (
    <Flex position="fixed" top="0" left="0" right="0" zIndex={200} bg={colors.tealHover} color="white"
      px="4" py="2" align="center" justify="center" gap="3" data-testid="update-banner">
      <Text fontSize="sm" fontWeight="500">A new version of Hakina Safety is ready.</Text>
      <Button size="xs" bg="white"
        onClick={() => reg.waiting.postMessage({ type: "SKIP_WAITING" })} data-testid="update-refresh-btn">
        Refresh
      </Button>
    </Flex>
  );
};
